import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef } from 'react';
import { ComboBox, ComboBoxStateContext } from 'react-aria-components';
import { ComboInput, ComboList } from './combo-parts.js';
import { cx } from './cx.js';
import { definedProps, fieldRootClassName } from './field-parts.js';
/**
 * A text field that offers suggestions while typing - a combobox (WAI-ARIA): the arrow keys walk
 * the list, Enter takes the one marked, Escape closes it, a screen reader hears the marked one.
 * What is typed stays whatever is picked or not: the list never replaces the field, and leaving it
 * keeps the text. The caller fetches the suggestions (debounced) and decides what a pick fills in.
 */
export function SuggestField({ label, placeholder, value, onChange, suggestions, onPick, footnote, description, errorMessage, isRequired, isReadOnly, maxLength, validate, name, className, }) {
    return (_jsxs(ComboBox, { items: suggestions, inputValue: value, onInputChange: onChange, 
        // Nothing stays selected: a pick fills the fields, and the field is text again.
        selectedKey: null, onSelectionChange: (key) => {
            const picked = suggestions.find((s) => s.id === key);
            if (picked)
                onPick(picked);
        }, allowsCustomValue: true, ...(validate ? { validate: ({ inputValue }) => validate(inputValue) } : {}), ...definedProps({ isRequired, isReadOnly, name }), className: cx(fieldRootClassName, className), children: [_jsx(ComboInput, { label: label, placeholder: placeholder, description: description, errorMessage: errorMessage, isRequired: isRequired, maxLength: maxLength }), _jsx(OpenWhenSuggested, { suggestions: suggestions }), _jsx(ComboList, { footnote: footnote })] }));
}
/**
 * Opens the list when suggestions arrive while the field still has focus. React Aria opens it
 * on typing only - the suggestions come a pause and a request later, and by then the person may
 * be in the next field, where an opening list would hide what they type.
 */
function OpenWhenSuggested({ suggestions }) {
    const state = useContext(ComboBoxStateContext);
    // The list each opening answered: closed by Escape, it stays closed until new ones arrive.
    const answered = useRef(null);
    useEffect(() => {
        if (!state || answered.current === suggestions)
            return;
        answered.current = suggestions;
        if (suggestions.length > 0 && state.isFocused && !state.isOpen)
            state.open(null, 'manual');
    }, [state, suggestions]);
    return null;
}
//# sourceMappingURL=suggest-field.js.map